import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Alert, Spin } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { logIn } from '../../redux/reducers/authSlice';
import { config } from '../../core/config';
import { beginMobileSso, clearSsoSession } from '../../services/mobileSso';
import loginLogo from '../../assets/images/login-logo.png';
import './LoginScreen.css';

interface LoginFormData {
  email: string;
  password: string;
}

const LoginScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetching, error } = useAppSelector((state) => state.auth);
  const [form] = Form.useForm();
  const [ssoStarting, setSsoStarting] = useState(false);
  const [ssoError, setSsoError] = useState<string | null>(null);

  useEffect(() => {
    const email = searchParams.get('email');
    if (email) {
      form.setFieldsValue({ email });
    }
    if (searchParams.get('focus') === 'password') {
      // Password field focus is handled by browser after render.
      window.setTimeout(() => {
        const el = document.querySelector<HTMLInputElement>('input[type="password"]');
        el?.focus();
      }, 0);
    }
  }, [searchParams, form]);

  const handleSubmit = async (values: LoginFormData) => {
    try {
      await dispatch(logIn(values)).unwrap();
    } catch {
      // Error is handled in the Redux slice
    }
  };

  const handleForgotPassword = () => {
    window.open(`https://login.${config.apiHost}/account/forgotpassword`, '_blank');
  };

  const handleSso = async () => {
    setSsoError(null);
    try {
      const email = await form.validateFields(['email']);
      setSsoStarting(true);
      const url = await beginMobileSso(email.email);
      navigate('/auth/redirecting', { state: { authorizeUrl: url } });
    } catch (err) {
      setSsoStarting(false);
      if (err && typeof err === 'object' && 'errorFields' in err) {
        return;
      }
      setSsoError('Unable to start SSO. Please try again.');
      clearSsoSession();
    }
  };

  return (
    <div className="login-container">
      <div className="login-header-container">
        <img
          src={loginLogo}
          alt="UPtime by SHzoom"
          className="login-logo"
        />
      </div>

      <div className="login-form-container">
        <div className="login-eyebrow">Sign in</div>
        <div className="login-subtitle">Access your incidents and repairs</div>

        <Form
          form={form}
          name="login"
          onFinish={handleSubmit}
          layout="vertical"
          size="large"
          autoComplete="off"
        >
          <Form.Item
            name="email"
            label="Work email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Work email"
              type="email"
              autoComplete="email"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please enter your password' },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Password"
              autoComplete="current-password"
            />
          </Form.Item>

          {(error || ssoError) && (
            <Form.Item>
              <Alert
                message="Login Failed"
                description={ssoError || error?.message || 'Invalid email or password'}
                type="error"
                showIcon
              />
            </Form.Item>
          )}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={fetching}
              block
              size="large"
            >
              Sign in
            </Button>
          </Form.Item>

          <Form.Item>
            <Button
              block
              size="large"
              onClick={handleSso}
              loading={ssoStarting}
              disabled={fetching}
            >
              Sign in with SSO
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center', marginTop: '8px' }}>
            <Button type="link" onClick={handleForgotPassword}>
              Forgot password?
            </Button>
          </div>
        </Form>
      </div>

      {(fetching || ssoStarting) && (
        <div className="login-loading">
          <Spin size="large" />
        </div>
      )}
    </div>
  );
};

export default LoginScreen;
